import PropTypes from "prop-types";
import * as React from "react";
import { rowsOf } from "../../../domain/productComposer/composer";
import { pricingFor } from "../../../domain/productComposer/pricing";
import { Alert, Button, SectionHead, Typography } from "../../../themeV2";
import ProblemList from "../sections/ProblemList";
import { cardTitle } from "./CardEditor";
import ListingPreview from "./ListingPreview";
import previewCss from "./ListingPreview.module.css";
import css from "./create.module.css";

/* The shop builder shows admins a listing's full price and talent their own split. */
const shownPrice = ({ signers, signedType, viewerId, isAdmin }) =>
  isAdmin
    ? signers.reduce((sum, s) => sum + (s.splits[signedType] ?? 0), 0)
    : signers.find((s) => s.influencerId === viewerId)?.splits[signedType] ?? 0;

const ReviewPage = function ReviewPage({
  state,
  bumpFacts,
  aspectRatio,
  productTypeName,
  isAdmin,
  problems,
  serverProblems,
  serverMessage,
  onEditStep,
}) {
  const rows = rowsOf(state);
  const cardsById = new Map(
    state.cards.map((card, index) => [card.cardId, { card, index }]),
  );

  return (
    <div className={css.page}>
      <ProblemList problems={problems} title="Fix these before creating" />
      <ProblemList
        problems={serverProblems}
        title="The server couldn't create these products"
      />
      {serverMessage && serverProblems.length === 0 && (
        <Alert severity="error">{serverMessage}</Alert>
      )}

      <section className={css.section}>
        <SectionHead
          title={`${rows.length} product${
            rows.length === 1 ? "" : "s"
          } will be created`}
          as="h3"
        >
          <Button variant="text" onClick={() => onEditStep("product")}>
            Edit products
          </Button>
          <Button variant="text" onClick={() => onEditStep("pricing")}>
            Edit pricing
          </Button>
        </SectionHead>
        <ul className={previewCss.grid}>
          {rows.map(({ cardId, signedType }) => {
            const { card, index } = cardsById.get(cardId);
            const [cover] = card.images;
            const { total } = pricingFor({
              splits: state.signers.map((s) => s.splits[signedType] ?? 0),
              signedType,
              ...bumpFacts,
            });

            return (
              <ListingPreview
                key={`${cardId}:${signedType}`}
                name={cardTitle(card, index)}
                imageUrl={cover?.imageUrl ?? cover?.previewUrl}
                aspectRatio={aspectRatio}
                signedType={signedType}
                price={shownPrice({
                  signers: state.signers,
                  signedType,
                  viewerId: state.shop.influencerId,
                  isAdmin,
                })}
                customerPays={total}
                stock={state.stock[signedType]}
                productTypeName={productTypeName}
              />
            );
          })}
        </ul>
        {state.tags.length > 0 && (
          <Typography variant="body-md" color="variant" as="p">
            Tagged {state.tags.map((t) => t.tagString).join(", ")}
          </Typography>
        )}
      </section>
    </div>
  );
};

ReviewPage.propTypes = {
  aspectRatio: PropTypes.number.isRequired,
  bumpFacts: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  onEditStep: PropTypes.func.isRequired,
  productTypeName: PropTypes.string.isRequired,
  problems: PropTypes.arrayOf(PropTypes.object).isRequired,
  serverMessage: PropTypes.string,
  serverProblems: PropTypes.arrayOf(PropTypes.object).isRequired,
  state: PropTypes.object.isRequired,
};

export default ReviewPage;
